import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@noor/lib/cn';

/**
 * Two visual treatments share the same Radix machinery:
 *
 * - `solid` (default) — a filled track (`bg-tabs-list-bg`) with equal-width
 *   triggers; the active trigger lifts onto a card surface. The classic
 *   segmented control.
 * - `pill` — the Figma tab bar (node 762:1676): a white `rounded-full` track
 *   (`p-2.5`) holding borderless 16px-medium soft-ink pills; a hovered pill
 *   gains the hairline border + near-black text; the active pill fills solid
 *   brand orange with white text. Matches the CPanel tabs in the design
 *   (Our services · Design Process · Pricing · …).
 *
 *   The pill trigger keeps CONSTANT metrics in every state — same shape
 *   (`rounded-full`), same weight, same size, same tracking, color-only
 *   transitions — so switching tabs flips only colors and the bar never
 *   re-balances.
 *
 * - `folder` — the Noor-CPanel merchant dialog bar: a 45px solid-orange track
 *   (`rounded-t-[12px]`) whose active trigger is a bottom-flush white folder
 *   tab (41px, `rounded-t-[8px]`, 14px-medium black) while idle triggers are
 *   32px white-on-orange chips. The bar glues onto a bordered content card
 *   (border-x + border-b, `rounded-b-[12px]`) rendered by the caller.
 *
 * The variant is chosen on `<Tabs>` and shared with the list + triggers through
 * context so callers set it in one place.
 */
type TabsVariant = 'solid' | 'pill' | 'folder';

const TabsVariantContext = React.createContext<TabsVariant>('solid');

function Tabs({
  className,
  variant = 'solid',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & { variant?: TabsVariant }) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root
        data-slot="tabs"
        data-variant={variant}
        className={cn('flex flex-col gap-2', className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        'inline-flex items-center',
        variant === 'solid'
          ? 'w-fit justify-center gap-1 rounded-lg bg-tabs-list-bg p-1'
          : variant === 'pill'
            ? 'w-fit flex-wrap gap-2 rounded-full bg-tabs-track-bg p-2.5'
            : 'h-[45px] w-full items-end gap-1 overflow-x-auto overflow-y-hidden rounded-t-[12px] bg-primary px-[2px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "inline-flex items-center justify-center gap-1.5 font-medium whitespace-nowrap text-tabs-trigger-fg transition-all duration-150 ease-out outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        variant === 'solid'
          ? 'flex-1 rounded-md px-3 py-1.5 text-sm data-[state=active]:rounded-md data-[state=active]:bg-tabs-trigger-bg-active data-[state=active]:text-tabs-trigger-fg-active data-[state=active]:shadow-sm'
          : variant === 'pill'
            ? 'gap-1 rounded-full border border-transparent px-2.5 py-2 text-[length:var(--tabs-pill-font-size)] leading-6 font-[var(--tabs-pill-font-weight)] tracking-[-0.32px] text-tabs-pill-fg transition-colors duration-150 ease-out hover:border-tabs-pill-border-hover hover:text-tabs-pill-fg-hover data-[state=active]:border-transparent data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:hover:text-primary-foreground'
            : 'mb-[6px] h-[32px] shrink-0 gap-1 rounded-[8px] px-4 py-2 text-sm font-normal tracking-[-0.28px] text-white transition-colors duration-150 ease-out hover:bg-white/10 data-[state=active]:relative data-[state=active]:z-10 data-[state=active]:mb-[-1px] data-[state=active]:h-[42px] data-[state=active]:rounded-t-[8px] data-[state=active]:rounded-b-none data-[state=active]:bg-white data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:hover:bg-white',
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(
        'mt-2 flex-1 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40',
        className,
      )}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
